import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import LoyaltyRewardsIcon from 'LuxKit/Icons/loyalty/LoyaltyRewardsIcon'
import { rem } from 'polished'
import styled from 'styled-components'

const DEFAULT_COLUMN_WIDTH = 168
const GUESTS_COLUMN_WIDTH = DEFAULT_COLUMN_WIDTH / 2

const Root = styled(VerticalSpacer)`
  width: 100%;
  min-width: 0;
`

const TableScroller = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding-inline: ${rem(12)};
  overflow-x: auto;
`

const TableCentered = styled.div`
  display: flex;
  justify-content: center;
  min-width: max-content;
`

const RewardsTable = styled.table`
  width: max-content;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
`

const HeaderCell = styled.th`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
  padding: ${rem(12)} ${rem(8)};
  background: ${props => props.theme.palette.neutral.default.eight};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  color: ${props => props.theme.palette.neutral.default.one};

  &.highlight {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    border-bottom: none;
    color: ${props => props.theme.palette.highlight.primary.darkBackground};
  }
`

const BodyCell = styled.td`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
  padding: ${rem(12)} ${rem(8)};
  vertical-align: middle;
  background: ${props => props.theme.palette.neutral.default.eight};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &.highlight {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    border-bottom: none;
  }
`

const TableRow = styled.tr`
  &:last-child ${BodyCell} {
    border-bottom: 0;
  }
`

const PackageCell = styled(BodyCell)`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
`

const GuestsCell = styled(BodyCell)`
  width: ${rem(GUESTS_COLUMN_WIDTH)};
  text-align: center;
`

const PriceCell = styled(BodyCell)`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
  text-align: center;
`

const PackageHeaderCell = styled(HeaderCell)`
  text-align: left;
`

const CenteredHeaderCell = styled(HeaderCell)`
  text-align: center;
`

const CenteredGuestsHeaderCell = styled(CenteredHeaderCell)`
  width: ${rem(GUESTS_COLUMN_WIDTH)};
`

function LoyaltyRewardsIntroTable() {
  const intl = useIntl()

  const packageColumnLabel = intl.formatMessage({
    id: 'LsogaAWZAMBbOjhV5hj0',
    defaultMessage: 'Maldives Package',
    description: 'Table header for the Maldives package column in the Société Rewards intro table',
  })
  const guestsColumnLabel = intl.formatMessage({
    id: '7qMp2gR_0UzJkQZMCEkV',
    defaultMessage: 'Guests',
    description: 'Table header for the guest count column in the Société Rewards intro table',
  })
  const rewardsColumnLabel = intl.formatMessage({
    id: 'oB6pz7BL-ozi_M3Ekmwq',
    defaultMessage: 'Société Rewards',
    description: 'Table header for the Société Rewards price column in the rewards intro table',
  })
  const standardColumnLabel = intl.formatMessage({
    id: 'NDgMZZugeakk_xUizkmt',
    defaultMessage: 'Standard Points + Pay',
    description: 'Table header for the standard points and cash column in the Société Rewards intro table',
  })
  const economyLabel = intl.formatMessage({
    id: '8_41AhuRcOgVxo92zxVp',
    defaultMessage: 'Economy',
    description: 'Economy fare row label in the Société Rewards intro table',
  })
  const economyPlusLabel = intl.formatMessage({
    id: 'AvrRazvSs4sZHx_KKwGG',
    defaultMessage: 'Economy Plus',
    description: 'Economy Plus fare row label in the Société Rewards intro table',
  })
  const businessLabel = intl.formatMessage({
    id: 'Xu-Dr-t5Q5bXYkCs7_hX',
    defaultMessage: 'Business',
    description: 'Business fare row label in the Société Rewards intro table',
  })
  const packageDescription = intl.formatMessage({
    id: 'Pv0TnChS4nAHsLGAamhE',
    defaultMessage: 'Return flights & 5 night stay',
    description: 'Package description shown under each fare class in the Société Rewards intro table',
  })
  const guestsLabel = intl.formatMessage({
    id: 'UdqKgoKVeNJhu5eIproe',
    defaultMessage: '2 adults',
    description: 'Guest count shown for each fare class in the Société Rewards intro table',
  })
  const disclaimer = intl.formatMessage({
    id: 'MMPKhsBXGE9YUU_8pghq',
    defaultMessage: '*Points required and prices shown are indicative only and may change at the time of booking.',
    description: 'Disclaimer below the hardcoded Société Rewards intro comparison table',
  })
  const economyRewardPointsLabel = intl.formatMessage({
    id: 'gsIkN5iZbg2VNeHG-xzp',
    defaultMessage: '120,000 pts',
    description: 'Hardcoded Société Rewards points value for economy fare in the intro table',
  })
  const economyRewardCashLabel = intl.formatMessage({
    id: 'kgXslPtJJFI1ywtfFgHZ',
    defaultMessage: '+ from $1,299/person',
    description: 'Hardcoded Société Rewards cash value for economy fare in the intro table',
  })
  const economyStandardPointsLabel = intl.formatMessage({
    id: 'WnnC2QD2wxmBKavCWjDD',
    defaultMessage: '998,891 pts',
    description: 'Hardcoded standard points value for economy fare in the intro table',
  })
  const economyStandardCashLabel = intl.formatMessage({
    id: 'm_ya4lPrsNhut2QUcuk6',
    defaultMessage: '+ from $1,100/person',
    description: 'Hardcoded standard cash value for economy fare in the intro table',
  })
  const economyPlusRewardPointsLabel = intl.formatMessage({
    id: 'zQV-AuaH-RA0ihfaviUm',
    defaultMessage: '200,000 pts',
    description: 'Hardcoded Société Rewards points value for economy plus fare in the intro table',
  })
  const economyPlusRewardCashLabel = intl.formatMessage({
    id: 'Pkte8UF9IedJsl1HNHre',
    defaultMessage: '+ from $1,399/person',
    description: 'Hardcoded Société Rewards cash value for economy plus fare in the intro table',
  })
  const economyPlusStandardPointsLabel = intl.formatMessage({
    id: 'c0WaVkmOLJgYQg0LI6L4',
    defaultMessage: '1,332,132 pts',
    description: 'Hardcoded standard points value for economy plus fare in the intro table',
  })
  const economyPlusStandardCashLabel = intl.formatMessage({
    id: 'qSjLNg2jaNGRX-F8-4sP',
    defaultMessage: '+ from $1,200/person',
    description: 'Hardcoded standard cash value for economy plus fare in the intro table',
  })
  const businessRewardPointsLabel = intl.formatMessage({
    id: 'WmZLgcagJ0GuGk1vYP1v',
    defaultMessage: '500,000 pts',
    description: 'Hardcoded Société Rewards points value for business fare in the intro table',
  })
  const businessRewardCashLabel = intl.formatMessage({
    id: '_a1AbjBAvE0qQ8ppIp93',
    defaultMessage: '+ from $1,799/person',
    description: 'Hardcoded Société Rewards cash value for business fare in the intro table',
  })
  const businessStandardPointsLabel = intl.formatMessage({
    id: 'k8tktFsEh28iHuY_Hfcl',
    defaultMessage: '3,730,092 pts',
    description: 'Hardcoded standard points value for business fare in the intro table',
  })
  const businessStandardCashLabel = intl.formatMessage({
    id: 'pEsyAThlCNX6CDkMCzPD',
    defaultMessage: '+ from $1,600/person',
    description: 'Hardcoded standard cash value for business fare in the intro table',
  })

  const rows = [
    {
      fareClass: economyLabel,
      rewardPoints: economyRewardPointsLabel,
      rewardCash: economyRewardCashLabel,
      standardPoints: economyStandardPointsLabel,
      standardCash: economyStandardCashLabel,
    },
    {
      fareClass: economyPlusLabel,
      rewardPoints: economyPlusRewardPointsLabel,
      rewardCash: economyPlusRewardCashLabel,
      standardPoints: economyPlusStandardPointsLabel,
      standardCash: economyPlusStandardCashLabel,
    },
    {
      fareClass: businessLabel,
      rewardPoints: businessRewardPointsLabel,
      rewardCash: businessRewardCashLabel,
      standardPoints: businessStandardPointsLabel,
      standardCash: businessStandardCashLabel,
    },
  ]

  return <Root gap={12}>
    <TableScroller>
      <TableCentered>
        <RewardsTable>
          <thead>
            <tr>
              <PackageHeaderCell>
                <BodyText variant="small" weight="bold">{packageColumnLabel}</BodyText>
              </PackageHeaderCell>
              <CenteredGuestsHeaderCell>
                <BodyText variant="small" weight="bold">{guestsColumnLabel}</BodyText>
              </CenteredGuestsHeaderCell>
              <CenteredHeaderCell className="highlight">
                <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
                  <LoyaltyRewardsIcon size={16} colour="highlight-primary-dark" />
                  <BodyText variant="small" weight="bold">{rewardsColumnLabel}</BodyText>
                </Group>
              </CenteredHeaderCell>
              <CenteredHeaderCell>
                <BodyText variant="small" weight="bold">{standardColumnLabel}</BodyText>
              </CenteredHeaderCell>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <TableRow key={row.fareClass}>
                <PackageCell>
                  <BodyText variant="small" weight="bold">{row.fareClass}</BodyText>
                  <BodyText variant="small">{packageDescription}</BodyText>
                </PackageCell>
                <GuestsCell>
                  <BodyText variant="small">{guestsLabel}</BodyText>
                </GuestsCell>
                <PriceCell className="highlight">
                  <BodyText variant="small" weight="bold">{row.rewardPoints}</BodyText>
                  <BodyText variant="small">{row.rewardCash}</BodyText>
                </PriceCell>
                <PriceCell>
                  <BodyText variant="small" weight="bold">{row.standardPoints}</BodyText>
                  <BodyText variant="small">{row.standardCash}</BodyText>
                </PriceCell>
              </TableRow>
            ))}
          </tbody>
        </RewardsTable>
      </TableCentered>
    </TableScroller>
    <Caption variant="medium" align="center">{disclaimer}</Caption>
  </Root>
}

export default LoyaltyRewardsIntroTable
